/**
 * V3 — Page Factures avec workflow Paiements.
 * Liste les factures, permet d'enregistrer/supprimer des paiements,
 * affiche montant_paye / solde_restant / statut (En attente/Partiel/Payé).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import {
  Download, Search, ChevronLeft, ChevronRight, FileText, CreditCard,
  Plus, Trash2, X, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const baseURL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};
const MODES_PAIEMENT = ['Cash', 'Mobile Money', 'Banque', 'Chèque'];

const STATUS_PILL = (s) => {
  const lower = (s || '').toLowerCase();
  if (lower === 'payé' || lower === 'paye') return 'v3-pill-in';
  if (lower === 'partiel') return 'v3-pill-cat';
  if (lower === 'annulé' || lower === 'annule') return 'v3-pill-out';
  return 'v3-pill-cat';
};

const EMPTY_PMT = {
  date: new Date().toISOString().slice(0, 10),
  montant: '',
  mode_paiement: 'Mobile Money',
  reference: '',
  notes: '',
};

export default function V3Factures() {
  const [data, setData] = useState({
    items: [], total: 0, total_pages: 0,
    kpis: { total_ttc: 0, total_paye: 0, total_solde_restant: 0, count: 0 },
    entities_available: [], statuts_available: [],
  });
  const [entity, setEntity] = useState('ALL');
  const [statut, setStatut] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Detail panel
  const [detailFact, setDetailFact] = useState(null);  // facture object
  const [pmtForm, setPmtForm] = useState(EMPTY_PMT);
  const [pmtError, setPmtError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDeletePmt, setConfirmDeletePmt] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { entity, page, page_size: 30 };
      if (statut) params.statut = statut;
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data: d } = await api.get('/api/v3/factures', { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [entity, statut, from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  const openDetail = async (f) => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/api/v3/factures/${f.id}`, { params: { entity: f.entity } });
      setDetailFact(d.facture);
      setPmtForm({ ...EMPTY_PMT, montant: '' });
      setPmtError(null);
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const refreshDetail = async () => {
    if (!detailFact) return;
    const { data: d } = await api.get(`/api/v3/factures/${detailFact.id}`, { params: { entity: detailFact.entity } });
    setDetailFact(d.facture);
    fetchData();
  };

  const submitPmt = async (e) => {
    e?.preventDefault?.();
    if (!detailFact) return;
    const m = Number(pmtForm.montant);
    if (!m || m <= 0) { setPmtError('Montant doit être > 0.'); return; }
    if (m > detailFact.solde_restant + 0.5) { setPmtError(`Montant > solde restant (${fmt(detailFact.solde_restant)}).`); return; }
    setBusy(true); setPmtError(null);
    try {
      await api.post(`/api/v3/factures/${detailFact.id}/paiements?entity=${encodeURIComponent(detailFact.entity)}`, {
        date: pmtForm.date,
        montant: m,
        mode_paiement: pmtForm.mode_paiement,
        reference: pmtForm.reference.trim() || null,
        notes: pmtForm.notes.trim() || null,
      });
      showToast(`Paiement ${fmt(m)} FCFA enregistré`);
      setPmtForm({ ...EMPTY_PMT, montant: '' });
      await refreshDetail();
    } catch (e) {
      setPmtError(e?.response?.data?.detail || e.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const doDeletePmt = async () => {
    if (!confirmDeletePmt || !detailFact) return;
    setBusy(true);
    try {
      await api.delete(`/api/v3/factures/${detailFact.id}/paiements/${confirmDeletePmt.id}?entity=${encodeURIComponent(detailFact.entity)}`);
      showToast(`Paiement supprimé`);
      setConfirmDeletePmt(null);
      await refreshDetail();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v3-page" data-testid="v3-factures">
      <div className="v3-page-head">
        <h1>Factures &amp; Paiements</h1>
        <p>{data.total} factures · CA TTC {fmt(data.kpis.total_ttc)} · Encaissé {fmt(data.kpis.total_paye)} · Reste {fmt(data.kpis.total_solde_restant)} FCFA</p>
      </div>

      <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <KpiCard icon={<FileText/>} label="CA total TTC" value={fmt(data.kpis.total_ttc) + ' FCFA'} accent="cyan" />
        <KpiCard icon={<CreditCard/>} label="Encaissé" value={fmt(data.kpis.total_paye) + ' FCFA'} accent="emerald" />
        <KpiCard icon={<AlertTriangle/>} label="Solde restant" value={fmt(data.kpis.total_solde_restant) + ' FCFA'} accent="amber" />
      </section>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => { setPage(1); setEntity(e.target.value); }} data-testid="filter-entity">
            <option value="ALL">Toutes</option>
            {data.entities_available.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Statut</label>
          <select value={statut} onChange={(e) => { setPage(1); setStatut(e.target.value); }} data-testid="filter-statut">
            <option value="">Tous</option>
            {data.statuts_available.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14}/>
            <input type="text" placeholder="Client, N°…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
          </div>
        </div>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entité</th>
                <th>N°</th>
                <th>Client</th>
                <th className="num">TTC</th>
                <th className="num">Payé</th>
                <th className="num">Solde</th>
                <th>Statut</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="v3-row" data-testid={`facture-row-${it.id}`}>
                  <td className="mono">{it.date}</td>
                  <td><span className="v3-entity-pill" style={{ background: (ENTITY_COLORS[it.entity] || '#94a3b8') + '20', color: ENTITY_COLORS[it.entity] || '#94a3b8' }}>{it.entity}</span></td>
                  <td className="mono">{it.numero}</td>
                  <td>{it.client || '—'}</td>
                  <td className="num">{fmt(it.montant_ttc)}</td>
                  <td className="num" style={{ color: '#86efac' }}>{fmt(it.montant_paye)}</td>
                  <td className="num strong" style={{ color: it.solde_restant > 0 ? '#fca5a5' : '#86efac' }}>{fmt(it.solde_restant)}</td>
                  <td><span className={`v3-pill ${STATUS_PILL(it.statut)}`}>{it.statut}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => openDetail(it)}
                      title="Détail & paiements"
                      aria-label="Détail"
                      data-testid={`detail-facture-${it.id}`}
                      className="v3-icon-btn"
                    ><CreditCard size={13} /></button>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={9} className="v3-empty">Aucune facture.</td></tr>}
              {loading && <tr><td colSpan={9} className="v3-empty">Chargement…</td></tr>}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}><ChevronLeft size={16}/>Préc.</button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages}>Suiv.<ChevronRight size={16}/></button>
          </div>
        )}
      </section>

      {/* Modal Détail + Paiements */}
      {detailFact && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDetailFact(null); }}>
          <div className="v3-modal v3-modal-wide" data-testid="facture-detail">
            <div className="v3-modal-head">
              <h2>Facture {detailFact.numero} · {detailFact.client}</h2>
              <button type="button" onClick={() => setDetailFact(null)} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <div className="v3-bl-totals" data-testid="facture-totals">
                <div><span>Montant TTC :</span><strong>{fmt(detailFact.montant_ttc)} FCFA</strong></div>
                <div><span>Payé :</span><strong style={{ color: '#86efac' }}>{fmt(detailFact.montant_paye)} FCFA</strong></div>
                <div className="v3-bl-totals-ttc"><span>Solde restant :</span><strong>{fmt(detailFact.solde_restant)} FCFA</strong></div>
              </div>

              <h3 style={{ margin: '20px 0 10px', fontSize: 13, color: '#94a3b8' }}>
                Paiements ({detailFact.paiements?.length || 0})
              </h3>
              {(detailFact.paiements && detailFact.paiements.length > 0) ? (
                <table className="v3-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Référence</th>
                      <th className="num">Montant</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailFact.paiements.map((p) => (
                      <tr key={p.id} data-testid={`paiement-row-${p.id}`}>
                        <td className="mono">{p.date}</td>
                        <td>{p.mode_paiement || p.modePaiement}</td>
                        <td className="mono">{p.reference || '—'}</td>
                        <td className="num strong" style={{ color: '#86efac' }}>{fmt(p.montant)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => setConfirmDeletePmt(p)}
                            aria-label="Supprimer paiement"
                            data-testid={`delete-paiement-${p.id}`}
                            className="v3-icon-btn v3-icon-btn-danger"
                          ><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="v3-empty">Aucun paiement.</div>}

              {detailFact.solde_restant > 0 && (
                <form onSubmit={submitPmt} style={{ marginTop: 20, padding: 16, background: 'rgba(0,0,0,0.2)', borderRadius: 12, border: '1px solid rgba(214,243,47,0.15)' }} data-testid="paiement-form">
                  <h4 style={{ fontSize: 12, color: '#d6f32f', marginBottom: 12 }}>+ Nouveau paiement</h4>
                  <div className="v3-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="v3-form-field">
                      <label className="v3-form-field-label">Date</label>
                      <input type="date" required value={pmtForm.date}
                        onChange={(e) => setPmtForm({ ...pmtForm, date: e.target.value })}
                        data-testid="paiement-date" />
                    </div>
                    <div className="v3-form-field">
                      <label className="v3-form-field-label">Montant</label>
                      <input type="number" min="0" step="1" required value={pmtForm.montant}
                        onChange={(e) => setPmtForm({ ...pmtForm, montant: e.target.value })}
                        placeholder={fmt(detailFact.solde_restant)} data-testid="paiement-montant" />
                    </div>
                    <div className="v3-form-field">
                      <label className="v3-form-field-label">Mode</label>
                      <select value={pmtForm.mode_paiement}
                        onChange={(e) => setPmtForm({ ...pmtForm, mode_paiement: e.target.value })}
                        data-testid="paiement-mode">
                        {MODES_PAIEMENT.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="v3-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="v3-form-field">
                      <label className="v3-form-field-label">Référence</label>
                      <input type="text" value={pmtForm.reference}
                        onChange={(e) => setPmtForm({ ...pmtForm, reference: e.target.value })}
                        placeholder="N° virement, chèque…" data-testid="paiement-ref" />
                    </div>
                    <div className="v3-form-field">
                      <label className="v3-form-field-label">Notes</label>
                      <input type="text" value={pmtForm.notes}
                        onChange={(e) => setPmtForm({ ...pmtForm, notes: e.target.value })}
                        placeholder="Notes facultatives" data-testid="paiement-notes" />
                    </div>
                  </div>
                  {pmtError && <div className="v3-form-error"><AlertTriangle size={14} /> {pmtError}</div>}
                  <div style={{ textAlign: 'right', marginTop: 12 }}>
                    <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="paiement-submit">
                      <Plus size={14}/>{busy ? 'Enregistrement…' : 'Ajouter le paiement'}
                    </button>
                  </div>
                </form>
              )}
              {detailFact.solde_restant <= 0 && (
                <div style={{ padding: 16, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, marginTop: 16, color: '#86efac', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <CheckCircle2 size={20} />
                  <strong>Facture intégralement payée.</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDeletePmt && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDeletePmt(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Supprimer ce paiement ?</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                Paiement du <b>{confirmDeletePmt.date}</b> · <b>{fmt(confirmDeletePmt.montant)} FCFA</b>
                {confirmDeletePmt.mode_paiement ? <> · {confirmDeletePmt.mode_paiement}</> : null}
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDeletePmt(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={doDeletePmt} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-paiement-btn">
                {busy ? '…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="facture-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}

      <style>{`
        .v3-modal-overlay { position: fixed; inset: 0; background: rgba(10,22,40,0.85); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .v3-modal { background: #0f1e3a; border: 1px solid rgba(214,243,47,0.15); border-radius: 18px; max-width: 540px; width: 100%; color: #e5e7eb; box-shadow: 0 30px 80px rgba(0,0,0,0.6); max-height: 90vh; display: flex; flex-direction: column; }
        .v3-modal-wide { max-width: 760px; }
        .v3-modal-confirm { max-width: 420px; }
        .v3-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .v3-modal-head h2 { font-size: 17px; font-weight: 800; color: #fff; letter-spacing: -0.3px; margin: 0; }
        .v3-modal-body { padding: 24px; overflow-y: auto; }
        .v3-modal-foot { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); }
        .v3-form-row { display: grid; gap: 14px; }
        .v3-form-error { margin-top: 14px; padding: 12px 14px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #fca5a5; font-size: 12px; border-radius: 8px; display: flex; align-items: center; gap: 8px; }
        .v3-form-field { margin-bottom: 16px; }
        .v3-form-field-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; color: #71717a; font-weight: 700; margin-bottom: 6px; }
        .v3-form-field input, .v3-form-field select, .v3-form-field textarea { width: 100%; padding: 12px 14px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: #fff; font: 500 13px Inter; transition: all .2s; }
        .v3-form-field input:focus, .v3-form-field select:focus, .v3-form-field textarea:focus { outline: none; border-color: #D6F32F; background: rgba(0,0,0,0.4); }
        .v3-bl-totals { padding: 14px 16px; background: rgba(0,0,0,0.3); border: 1px solid rgba(214,243,47,0.15); border-radius: 10px; display: flex; flex-direction: column; gap: 6px; }
        .v3-bl-totals > div { display: flex; justify-content: space-between; font: 500 12px Inter; color: #94a3b8; }
        .v3-bl-totals > div strong { color: #fff; font-weight: 700; }
        .v3-bl-totals-ttc { padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); color: #d6f32f !important; font-size: 14px !important; }
        .v3-bl-totals-ttc strong { color: #d6f32f !important; font-weight: 800 !important; font-size: 14px; }
        .v3-toast { position: fixed; bottom: 24px; right: 24px; padding: 14px 20px; background: #0f1e3a; color: #fff; border-radius: 12px; display: inline-flex; align-items: center; gap: 10px; font: 600 13px Inter; z-index: 2000; box-shadow: 0 12px 32px rgba(0,0,0,0.5); }
        .v3-toast-success { border: 1px solid rgba(34,197,94,0.4); color: #86efac; }
        .v3-toast-error { border: 1px solid rgba(239,68,68,0.4); color: #fca5a5; }
        .v3-icon-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #cbd5e1; padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; justify-content: center; }
        .v3-icon-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .v3-icon-btn-danger:hover { background: rgba(239,68,68,0.1); color: #fca5a5; }
      `}</style>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
